import type { ArticlesResponse } from '~/types/api/Article'
import type { EstateListingResponse } from '~/types/api/EstateListing'
import { useUpdateUrlQuery } from '~/composables/useUpdateUrlQuery'

export function usePaginationState({
  type,
  onPageChange,
}: {
  type: 'estate' | 'articles'
  onPageChange?: () => void
}) {
  let listingResponse = null as any

  const updateUrlQuery = useUpdateUrlQuery()

  const initialPage = updateUrlQuery.getQueryByKey('pagination')?.page || 1
  const onPageChangeCallback = ref(onPageChange) as Ref<() => void>
  const loadMoreStart = ref(null) as Ref<number | null>
  const lastPagesData = ref<any[]>([])
  const pageSize = 12
  const itemsCount = ref(0)
  const currentPage = ref(initialPage)
  const currentPageCount = computed(() => {
    const additionalCount = lastPagesData.value.length * pageSize

    if (pagesCount.value === 0) {
      return 0
    }

    if (currentPage.value === pagesCount.value) {
      return (
        itemsCount.value - (currentPage.value - 1) * pageSize + additionalCount
      )
    }

    return pageSize + additionalCount
  })

  const nextPageCount = computed(() => {
    const remainingCount = itemsCount.value - currentPage.value * pageSize

    if (remainingCount < pageSize) {
      return remainingCount
    }

    return pageSize
  })

  const hasLastPagesData = computed(() => lastPagesData.value.length > 0)

  function setResponse(
    response: ReturnType<
      typeof useAsyncData<EstateListingResponse | ArticlesResponse | null>
    >
  ) {
    listingResponse = response

    if (response.data.value?.count === undefined) {
      return
    }

    itemsCount.value = response.data.value.count

    if (currentPage.value > pagesCount.value) {
      currentPage.value = pagesCount.value < 1 ? 1 : pagesCount.value

      updateUrlQuery.setQueryByKey({
        key: 'pagination',
        value: currentPage.value === 1 ? null : { page: currentPage.value },
      })
    }
  }

  function setOnPageChangeCallback(callback: () => void) {
    onPageChangeCallback.value = callback
  }

  const pagesCount = computed(() => Math.ceil(itemsCount.value / pageSize))
  const hasPrev = computed(() => currentPage.value > 1)
  const hasNext = computed(() => currentPage.value < pagesCount.value)
  const hasLoadMore = computed(() => currentPage.value < pagesCount.value)

  const isNumberLoading = ref(false) as Ref<number | false>
  const isMoreLoading = ref(false) as Ref<boolean>

  const visiblePages = computed(() => {
    const items = []

    for (let i = 1; i <= pagesCount.value; i++) {
      if (i === 1 || i === pagesCount.value) {
        items.push({
          type: 'page',
          number: i,
        })

        continue
      }

      if (
        (i >= currentPage.value - 1 && i <= currentPage.value + 1) ||
        (currentPage.value === 1 && i <= currentPage.value + 2) ||
        (currentPage.value === pagesCount.value && i >= currentPage.value - 2)
      ) {
        items.push({
          type: 'page',
          number: i,
        })

        continue
      }

      if (
        i === currentPage.value - 2 ||
        i === currentPage.value + 2 ||
        (currentPage.value === 1 && i <= currentPage.value + 3) ||
        (currentPage.value === pagesCount.value && i >= currentPage.value - 3)
      ) {
        items.push({
          type: 'ellipsis',
          number: 0,
        })

        continue
      }
    }

    return items
  })

  async function setPage(page: number, refreshListing: boolean = true) {
    if (
      !listingResponse ||
      listingResponse.pending.value ||
      page < 1 ||
      page > pagesCount.value ||
      page === currentPage.value
    ) {
      return
    }

    isNumberLoading.value = page

    currentPage.value = page
    lastPagesData.value = []

    updateUrlQuery.setQueryByKey({
      key: 'pagination',
      value: currentPage.value === 1 ? null : { page: currentPage.value },
      routerActionType: refreshListing ? 'push' : 'replace',
    })

    if (refreshListing) {
      if (typeof onPageChangeCallback.value === 'function') {
        onPageChangeCallback.value()
      }

      await listingResponse.refresh()
    }

    isNumberLoading.value = false
  }

  function setNextPage() {
    if (!hasNext.value) return

    setPage(currentPage.value + 1)
  }

  function setPrevPage() {
    if (!hasPrev.value) return

    setPage(currentPage.value - 1)
  }

  async function onLoadMore() {
    if (!listingResponse || listingResponse.pending.value) return
    if (!hasLoadMore.value) return

    isMoreLoading.value = true

    if (loadMoreStart.value === null) {
      loadMoreStart.value = currentPage.value
    }

    const tmpPrevData = [...listingResponse.data.value.data]
    currentPage.value = currentPage.value + 1

    await listingResponse.refresh()

    updateUrlQuery.setQueryByKey({
      key: 'pagination',
      value: {
        page: currentPage.value,
        start: loadMoreStart.value,
      },
      routerActionType: 'push',
    })

    nextTick(() => {
      lastPagesData.value.push(tmpPrevData)
    })

    isMoreLoading.value = false
  }

  return {
    type,
    state: {
      pageSize,
      loadMoreStart,
      currentPage,
      currentPageCount,
      nextPageCount,
      itemsCount,
      pagesCount,
      hasPrev,
      hasNext,
      hasLoadMore,
      visiblePages,
      lastPagesData,
      hasLastPagesData,
      isNumberLoading,
      isMoreLoading,
    },
    actions: {
      setResponse,
      setOnPageChangeCallback,
      setNextPage,
      setPrevPage,
      setPage,
      onLoadMore,
    },
  }
}
